// Lista de atividades da empresa (23/09/2026) — o que aparece no 📚 do campo
// "O que você fez", em contrato e em backoffice. Só quem tem horas.admin vê
// esta aba.
//
// Item não se apaga: lançamento antigo continua ligado a ele. "Tirar da
// lista" desativa — some da escolha, fica no histórico e pode voltar.
import { useCallback, useEffect, useState } from 'react';
import { App as AntApp, Button, Card, Input, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { api, type AtividadeTipo } from './api';

const { Text } = Typography;
type Linha = AtividadeTipo & { ordem: number; ativo: boolean };

export default function AtividadesLista() {
  const { message } = AntApp.useApp();
  const [itens, setItens] = useState<Linha[]>([]);
  const [nomes, setNomes] = useState<Record<number, string>>({});
  const [novo, setNovo] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [indisponivel, setIndisponivel] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const d = await api('/financeiro/horas/tipos-atividade');
      setItens(d.atividades || []);
      setIndisponivel(d.disponivel === false);
      setNomes({});
    } catch (e: any) { message.error(e.message); }
    finally { setCarregando(false); }
  }, [message]);
  useEffect(() => { carregar(); }, [carregar]);

  async function mudar(id: number, corpo: Record<string, unknown>, ok?: string) {
    try {
      await api(`/financeiro/horas/tipos-atividade/${id}`, { method: 'PATCH', body: JSON.stringify(corpo) });
      if (ok) message.success(ok);
      await carregar();
    } catch (e: any) { message.error(e.message); }
  }

  async function incluir() {
    const nome = novo.trim();
    if (nome.length < 2) { message.warning('Escreva o nome da atividade.'); return; }
    try {
      await api('/financeiro/horas/tipos-atividade', { method: 'POST', body: JSON.stringify({ nome }) });
      setNovo('');
      message.success(`"${nome}" entrou na lista`);
      await carregar();
    } catch (e: any) { message.error(e.message); }
  }

  // Trocar de lugar com o vizinho (ordem da lista no 📚).
  async function mover(i: number, dir: -1 | 1) {
    const ativos = itens.filter(x => x.ativo);
    const a = ativos[i]; const b = ativos[i + dir];
    if (!a || !b) return;
    try {
      await api(`/financeiro/horas/tipos-atividade/${a.id}`, { method: 'PATCH', body: JSON.stringify({ ordem: b.ordem }) });
      await api(`/financeiro/horas/tipos-atividade/${b.id}`, { method: 'PATCH', body: JSON.stringify({ ordem: a.ordem }) });
      await carregar();
    } catch (e: any) { message.error(e.message); }
  }

  const ativos = itens.filter(x => x.ativo);

  return (
    <Card styles={{ body: { padding: 16 } }}>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        É a lista que aparece no 📚 de "O que você fez", para contrato e para backoffice. A pessoa escolhe daqui
        e pode completar o texto ("Relatório — capítulo 3" continua contando como Relatório).
      </Text>
      {indisponivel && (
        <Text type="warning" style={{ display: 'block', marginBottom: 12 }}>
          A lista ainda está sendo instalada no banco. Volte em alguns minutos.
        </Text>
      )}
      <Space.Compact style={{ width: '100%', maxWidth: 520, marginBottom: 12 }}>
        <Input value={novo} onChange={e => setNovo(e.target.value)} onPressEnter={incluir}
          placeholder="Nova atividade (ex.: Topografia)" maxLength={80} />
        <Button type="primary" icon={<PlusOutlined />} onClick={incluir}>Incluir</Button>
      </Space.Compact>
      <Table
        rowKey="id" size="small" loading={carregando} pagination={false} dataSource={itens}
        columns={[
          {
            title: 'Atividade', key: 'nome',
            render: (_: any, r: Linha) => {
              const editado = nomes[r.id];
              const mudou = editado !== undefined && editado.trim() !== r.nome;
              return (
                <Space.Compact style={{ width: '100%', maxWidth: 420 }}>
                  <Input value={editado ?? r.nome} maxLength={80}
                    onChange={e => setNomes(n => ({ ...n, [r.id]: e.target.value }))}
                    onPressEnter={() => mudou && mudar(r.id, { nome: editado }, 'Nome trocado')} />
                  {mudou && (
                    <Button icon={<SaveOutlined />} onClick={() => mudar(r.id, { nome: editado }, 'Nome trocado')}>Salvar</Button>
                  )}
                </Space.Compact>
              );
            },
          },
          {
            title: 'Na lista', key: 'ativo', width: 110,
            render: (_: any, r: Linha) => (
              <Tooltip title={r.ativo ? 'Tirar da lista (os lançamentos antigos continuam ligados)' : 'Voltar para a lista'}>
                <Switch checked={r.ativo} onChange={(v) => mudar(r.id, { ativo: v }, v ? 'Voltou para a lista' : 'Saiu da lista')} />
              </Tooltip>
            ),
          },
          {
            title: 'Ordem', key: 'ordem', width: 96,
            render: (_: any, r: Linha) => {
              if (!r.ativo) return <Tag>fora</Tag>;
              const i = ativos.findIndex(x => x.id === r.id);
              return (
                <Space size={2}>
                  <Button size="small" icon={<ArrowUpOutlined />} disabled={i <= 0} onClick={() => mover(i, -1)} />
                  <Button size="small" icon={<ArrowDownOutlined />} disabled={i >= ativos.length - 1} onClick={() => mover(i, 1)} />
                </Space>
              );
            },
          },
        ]}
      />
    </Card>
  );
}
